import { colorsPalettes, type StrictColor } from "@similarweb/styles";

import type { ExtensionAdoptionStatsData } from "../types";

export type AdoptionHealthStatus = "strong" | "moderate" | "attention" | "critical";

type AdoptionHealthColors = {
    bar: StrictColor;
    badge: StrictColor;
    badgeBg: StrictColor;
};

const HUNDRED_PERCENT = 100;

export const HEALTH_STATUS_COLORS: Record<AdoptionHealthStatus, AdoptionHealthColors> = {
    strong: {
        bar: colorsPalettes.mint["500"],
        badge: colorsPalettes.mint["500"],
        badgeBg: colorsPalettes.mint["100"],
    },
    moderate: {
        bar: colorsPalettes.orange["400"],
        badge: colorsPalettes.orange["400"],
        badgeBg: colorsPalettes.orange["100"],
    },
    attention: {
        bar: colorsPalettes.red["400_P"],
        badge: colorsPalettes.red["400_P"],
        badgeBg: colorsPalettes.red["100"],
    },
    critical: {
        bar: colorsPalettes.red["400_P"],
        badge: colorsPalettes.red["400_P"],
        badgeBg: colorsPalettes.red["100"],
    },
} as const;

type HealthThresholds = { strong: number; moderate: number; attention: number };

const HEALTH_THRESHOLDS: HealthThresholds = { strong: 0.7, moderate: 0.5, attention: 0.25 };

const getHealthStatus = (ratio: number, thresholds: HealthThresholds): AdoptionHealthStatus => {
    if (ratio >= thresholds.strong) {
        return "strong";
    }
    if (ratio >= thresholds.moderate) {
        return "moderate";
    }
    if (ratio >= thresholds.attention) {
        return "attention";
    }
    return "critical";
};

const safeRatio = (numerator: number, denominator: number): number => (denominator <= 0 ? 0 : numerator / denominator);

export const getAdoptionStatus = (stats: ExtensionAdoptionStatsData): AdoptionHealthStatus =>
    getHealthStatus(safeRatio(stats.installs, stats.totalSeats), HEALTH_THRESHOLDS);

export const getEngagementStatus = (stats: ExtensionAdoptionStatsData): AdoptionHealthStatus =>
    getHealthStatus(safeRatio(stats.activeUsers, stats.installs), HEALTH_THRESHOLDS);

export const getTrueHealthStatus = (stats: ExtensionAdoptionStatsData): AdoptionHealthStatus =>
    getHealthStatus(safeRatio(stats.activeUsers, stats.totalSeats), HEALTH_THRESHOLDS);

export const getAdoptionPercentage = (stats: ExtensionAdoptionStatsData): number =>
    Math.min(HUNDRED_PERCENT, Math.round(safeRatio(stats.installs, stats.totalSeats) * HUNDRED_PERCENT));

export const getEngagementPercentage = (stats: ExtensionAdoptionStatsData): number =>
    Math.min(HUNDRED_PERCENT, Math.round(safeRatio(stats.activeUsers, stats.installs) * HUNDRED_PERCENT));

export const getTrueHealthPercentage = (stats: ExtensionAdoptionStatsData): number =>
    Math.min(HUNDRED_PERCENT, Math.round(safeRatio(stats.activeUsers, stats.totalSeats) * HUNDRED_PERCENT));
